import { Component, OnInit, OnDestroy } from '@angular/core';
import { AddUserService } from './add-user.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule]
})
export class AddUserComponent implements OnInit, OnDestroy {
  permissionSets: { permissionSetId: string; name: string }[] = [];
  selectedRoles: string[] = [];
  private permissionSetsSub?: Subscription;
  isSubmitting = false;
  submitError: string | null = null;
  submitSuccess = false;
  orgId: string | null = null;
  orgName: string | null = null;
  userForm: FormGroup;
  showPassword = false;

  constructor(
    private addUserService: AddUserService,
    private fb: FormBuilder,
    private orgState: OrganizationStateService,
    private route: ActivatedRoute
  ) {
    this.userForm = this.fb.group({
      firstName: [`firstName${this.addUserService.randomString(6)}`, Validators.required],
      lastName: [`lastName${this.addUserService.randomString(6)}`, Validators.required],
      externalId: [`externalId${this.addUserService.randomString(6)}`],
      npiNumber: [this.addUserService.randomNpiNumber()],
      email: [`email${this.addUserService.randomString(6)}@ymail.com`, [Validators.email]],
      username: ['', Validators.required], // Start with empty username, will be generated
      password: ['', Validators.required], // Start with empty password, will be generated
      active: [true],
      roles: [[], Validators.required],
      excludeReferrals: [false] // <-- Added field for checkbox
    });
  }

  ngOnInit(): void {
    this.initializeRouteHandling();
    this.initializeFormHandling();
    this.initializeGeneratedFields();
  }

  private initializeRouteHandling(): void {
    this.route.paramMap.subscribe(params => {
      const orgIdFromRoute = params.get('orgId');
      if (orgIdFromRoute) {
        this.handleRouteWithOrgId(orgIdFromRoute);
      } else {
        this.handleRouteWithoutOrgId();
      }
    });
  }

  private handleRouteWithOrgId(orgId: string): void {
    this.orgId = orgId;
    this.orgState.orgResponse$.subscribe(org => {
      this.orgName = org?.name || null;
    }).unsubscribe();
    this.addUserService.getPermissionSets(this.orgId).subscribe(sets => {
      this.permissionSets = this.addUserService['normalizePermissionSets'](sets);
    });
  }

  private handleRouteWithoutOrgId(): void {
    this.addUserService.getOrgIdAndPermissionSets().subscribe(({ orgId, permissionSets }) => {
      this.orgId = orgId;
      this.permissionSets = permissionSets;
    });
    this.orgState.orgResponse$.subscribe(org => {
      this.orgName = org?.name || null;
    }).unsubscribe();
  }

  private initializeFormHandling(): void {
    this.userForm.get('roles')?.valueChanges.subscribe(val => {
      this.selectedRoles = val || [];
    });
  }

  private initializeGeneratedFields(): void {
    setTimeout(() => {
      this.onRefreshUsername();
      this.onRefreshPassword();
    }, 100);
  }

  onRefreshUsername(): void {
    const firstName = this.userForm.get('firstName')?.value?.trim();
    const lastName = this.userForm.get('lastName')?.value?.trim();

    if (!this.areNamesValid(firstName, lastName)) {
      return;
    }
    
    console.log(`Generating username for: ${firstName} ${lastName}`);
    
    this.addUserService.generateUsername(firstName, lastName).subscribe({
      next: (response) => {
        console.log('Username generation response:', response);
        if (response?.username) {
          console.log(`Setting username to: ${response.username}`);
          this.userForm.patchValue({ username: response.username });
        } else {
          console.warn('No username in response, using fallback');
          this.setFallbackUsername(firstName, lastName);
        }
      },
      error: (error) => {
        console.warn('Failed to generate username:', error);
        this.setFallbackUsername(firstName, lastName);
      }
    });
  }

  private areNamesValid(firstName: string, lastName: string): boolean {
    return firstName && lastName && firstName.length > 0 && lastName.length > 0;
  }

  private setFallbackUsername(firstName: string, lastName: string): void {
    const fallbackUsername = `${firstName.toLowerCase()}.${lastName.toLowerCase()}${this.addUserService.randomString(4)}`;
    console.log(`Using fallback username: ${fallbackUsername}`);
    this.userForm.patchValue({ username: fallbackUsername });
  }

  onFirstNameBlur(): void {
    console.log('First name blur triggered');
    this.userForm.get('firstName')?.markAsTouched();
    this.tryAutoGenerateUsername();
  }

  onLastNameBlur(): void {
    console.log('Last name blur triggered');
    this.userForm.get('lastName')?.markAsTouched();
    this.tryAutoGenerateUsername();
  }

  private tryAutoGenerateUsername(): void {
    const firstName = this.userForm.get('firstName')?.value?.trim();
    const lastName = this.userForm.get('lastName')?.value?.trim();
    
    console.log('tryAutoGenerateUsername called with names:', { firstName, lastName });
    
    // Only auto-generate if both names are valid
    if (!this.areNamesValid(firstName, lastName)) {
      console.log('Names not valid, skipping auto-generation');
      return;
    }

    console.log('Names are valid, proceeding with auto-generation');
    this.generateUsernameInternal(firstName, lastName);
  }

  private generateUsernameInternal(firstName: string, lastName: string): void {
    console.log(`Auto-generating username for: ${firstName} ${lastName}`);
    
    this.addUserService.generateUsername(firstName, lastName).subscribe({
      next: (response) => {
        console.log('Auto-generated username response:', response);
        if (response?.username) {
          console.log(`Auto-setting username to: ${response.username}`);
          this.userForm.patchValue({ username: response.username });
        } else {
          console.warn('No username in auto-generation response, using fallback');
          this.setFallbackUsername(firstName, lastName);
        }
      },
      error: (error) => {
        console.warn('Failed to auto-generate username:', error);
        this.setFallbackUsername(firstName, lastName);
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onRefreshPassword(): void {
    console.log('Generating new password...');
    
    this.addUserService.generatePassword().subscribe({
      next: (response) => {
        console.log('Password generation response:', response);
        if (response?.userPassword) {
          this.userForm.patchValue({ password: response.userPassword });
        } else {
          console.warn('No userPassword in response, using fallback');
          this.setFallbackPassword();
        }
      },
      error: (error) => {
        console.warn('Failed to generate password:', error);
        this.setFallbackPassword();
      }
    });
  }

  private setFallbackPassword(): void {
    const fallbackPassword = `Password@${this.addUserService.randomString(8)}`;
    console.log(`Using fallback password: ${fallbackPassword}`);
    this.userForm.patchValue({ password: fallbackPassword });
  }

  ngOnDestroy(): void {
    this.permissionSetsSub?.unsubscribe();
  }

  onSubmit() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    
    this.setSubmissionState(true);
    const userPayload = this.buildUserPayload();
    
    this.addUserService.addUser(userPayload).subscribe({
      next: () => this.handleSubmissionSuccess(),
      error: (err) => this.handleSubmissionError(err)
    });
  }

  private setSubmissionState(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    this.submitError = null;
    this.submitSuccess = false;
  }

  private buildUserPayload(): any {
    const formValue = this.userForm.value;
    return {
      firstName: formValue.firstName || 'textFirstName',
      lastName: formValue.lastName || 'textLastName',
      externalId: formValue.externalId || 'textExternalId',
      npiNumber: formValue.npiNumber || '0123456789',
      email: formValue.email || `email_${Date.now()}@ymail.com`,
      username: formValue.username || `user_${Date.now()}`,
      password: formValue.password || `strsing@1!${Math.floor(Math.random() * 1000000)}`,
      excludeReferrals: !!formValue.excludeReferrals,
      authorizedOrgIds: [this.orgId || "3fa85f64-5717-4562-b3fc-2c963f66afa6"],
      active: formValue.active ?? true,
      orgId: this.orgId,
      selectedRoles: this.getValidatedRoles()
    };
  }

  private getValidatedRoles(): string[] {
    return this.selectedRoles && this.selectedRoles.length > 0 
      ? this.selectedRoles 
      : ["3fa85f64-5717-4562-b3fc-2c963f66afa6"];
  }

  private handleSubmissionSuccess(): void {
    this.isSubmitting = false;
    this.submitSuccess = true;
  }

  private handleSubmissionError(err: any): void {
    this.isSubmitting = false;
    this.submitError = err?.error?.message || 'Failed to add user.';
  }
}